import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    useGetDistributorCompaniesQuery,
    useGetDistributorUsersQuery,
    useMoveSubscriptionMutation,
} from 'api/main';
import { distributorSchemas } from 'schemas';
import { useDebounce } from 'utils/hooks';
import { ORDER_BY, conventionStatus } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomSelect, Loader } from 'components';
import {
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { parseFullName } from 'utils/helpers';

const PartnerDialog = ({ open, handleClose, currentSubscription }) => {
    const [selectedCompany, setSelectedCompany] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [keyword, setKeyword] = useState('');
    const [error, setError] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);

    const { data: { distributors = [] } = { distributors: [] }, isFetching: distributorLoader } =
        useGetDistributorCompaniesQuery(
            distributorSchemas.getDistributorList({
                pagination: false,
                keyword: debouncedKeyword,
                oneOrMoreConventionsFullySigned: 1,
                orderBy: ORDER_BY.distributorCompanyName,
            }),
            { skip: debouncedKeyword === '' }
        );

    const { data: users = [], isFetching: usersLoader } = useGetDistributorUsersQuery(
        { companyId: selectedCompany?.id },
        { skip: !selectedCompany }
    );

    const [moveSubscription, { isSuccess: moveSuccess, isLoading: loading, reset }] =
        useMoveSubscriptionMutation();

    const filteredDistributors = useMemo(
        () =>
            distributors.filter(d =>
                d[0].conventions.find(
                    c =>
                        c.status === conventionStatus.FULLY_SIGNED &&
                        c.investmentFund.id === currentSubscription.investmentFund.id
                )
            ),
        [distributors, currentSubscription]
    );

    const handleConfirm = useCallback(() => {
        if (selectedUser) {
            moveSubscription({
                distributorId: selectedUser,
                id: currentSubscription?.id,
            });
        } else {
            setError(texts.CONSULTANT_REQUIRED);
        }
    }, [moveSubscription, selectedUser, currentSubscription]);

    const handleAutocompleteChange = useCallback((e, newValue) => {
        setSelectedCompany(newValue);
        setKeyword(newValue?.corporateName);
    }, []);

    const handleSearchChange = useCallback((e, newValue) => {
        setSelectedCompany('');
        setSelectedUser('');
        setKeyword(newValue);
    }, []);

    const handleUserChange = useCallback(e => {
        setError('');
        setSelectedUser(+e.target.value);
    }, []);

    useEffect(() => {
        if (!open) {
            setSelectedUser('');
            setKeyword('');
        }
    }, [open]);

    useEffect(() => {
        if (moveSuccess) {
            reset();
            handleClose();
        }
    }, [moveSuccess, reset, handleClose]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.CHANGE_PARTNER} handleClose={handleClose} />
            <DialogContent>
                <Autocomplete
                    autoComplete
                    disableClearable
                    openOnFocus
                    loading={distributorLoader}
                    loadingText={<Loader size={20} marginTop={0} staticOnly />}
                    forcePopupIcon={false}
                    id="autocomplete-distributor-company"
                    getOptionLabel={option => {
                        return typeof option === 'string' ? option : option.corporateName;
                    }}
                    isOptionEqualToValue={(option, value) => {
                        if (!filteredDistributors.length) return true;
                        return (
                            !option ||
                            (typeof option === 'string'
                                ? option === value
                                : option.corporateName === value)
                        );
                    }}
                    options={filteredDistributors.map(d => d[0])}
                    clearIcon={null}
                    noOptionsText={<Typography>{texts.NO_OPTIONS}</Typography>}
                    value={keyword || null}
                    onChange={handleAutocompleteChange}
                    onInputChange={handleSearchChange}
                    renderInput={params => (
                        <TextField
                            ref={params.InputProps.ref}
                            variant="outlined"
                            inputProps={params.inputProps}
                            placeholder={texts.SEARCH_FOR_PARTNER}
                        />
                    )}
                    renderOption={(props, option) => (
                        <MenuItem key={option.id} id={option.id} {...props}>
                            {option?.corporateName}
                        </MenuItem>
                    )}
                />
                <Loader loading={usersLoader}>
                    <CustomSelect
                        label={texts.CONSULTANT}
                        disabled={!selectedCompany}
                        onChange={handleUserChange}
                        value={selectedUser}
                        error={!!error}
                        helperText={error}
                    >
                        {!users.length && (
                            <MenuItem value={0} disabled>
                                {texts.NO_OPTIONS}
                            </MenuItem>
                        )}
                        {users.map(u => (
                            <MenuItem key={u.id} value={u.id}>
                                {parseFullName(u)}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                </Loader>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={loading} onClick={handleClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton onClick={handleConfirm} loading={loading}>
                    {texts.CONFIRMER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default PartnerDialog;
